import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, ArrowRight, Check, MailCheck, X } from 'lucide-react';
import { api, ApiError } from '../services/api';
import { Logo } from '../components/Logo';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Alert } from '../components/Alert';
import { Input } from '../components/Input';
import { SignupView } from '../types';

/**
 * SPEC-07 §4.1 — account creation, reachable from checkout and from invitation links.
 *
 * Registration is disabled in CYRN Auth; Nexus creates the account and checks
 * the emailed code, then hands over to CYRN Auth sign-in with the email filled in.
 */

type Lang = 'fr' | 'en';

const T = {
  fr: {
    title: 'Créez votre compte CYRN',
    subtitle: 'Un seul compte pour tous les services CYRN.',
    givenName: 'Prénom',
    familyName: 'Nom',
    email: 'Adresse e-mail',
    password: 'Mot de passe',
    rules: ['8 caractères minimum', 'Une majuscule et une minuscule', 'Un chiffre', 'Un symbole'],
    terms: 'J’accepte les',
    termsLink: 'conditions générales',
    privacy: 'et la',
    privacyLink: 'politique de confidentialité',
    create: 'Créer mon compte',
    haveAccount: 'Déjà un compte ?',
    signIn: 'Se connecter',
    codeTitle: 'Vérifiez votre e-mail',
    codeBody: 'Nous avons envoyé un code à',
    code: 'Code de confirmation',
    confirm: 'Confirmer',
    resend: 'Renvoyer le code',
    resendIn: 'Renvoyer dans',
    resent: 'Un nouveau code a été envoyé.',
    wrongEmail: 'Ce n’est pas la bonne adresse ?',
    restart: 'Recommencer',
    errors: {
      account_exists: 'Un compte existe déjà avec cette adresse. Connectez-vous pour continuer.',
      weak_password: 'Ce mot de passe ne respecte pas les règles ci-dessous.',
      terms_required: 'Veuillez accepter les conditions générales.',
      rate_limited: 'Trop de tentatives. Réessayez dans une heure.',
      signup_unavailable: 'L’inscription est momentanément indisponible. Écrivez-nous à contact@cyrn.fr.',
      auth_unavailable: 'Le service de comptes ne répond pas. Réessayez dans un instant.',
      invalid_code: 'Code incorrect.',
      too_many_attempts: 'Trop de codes incorrects. Demandez un nouveau code.',
      too_many_codes: 'Trop de codes envoyés. Recommencez plus tard.',
      resend_too_soon: 'Patientez un peu avant de redemander un code.',
      signup_expired: 'Cette inscription a expiré. Recommencez.',
      generic: 'Une erreur est survenue.',
    },
  },
  en: {
    title: 'Create your CYRN account',
    subtitle: 'One account for every CYRN service.',
    givenName: 'First name',
    familyName: 'Last name',
    email: 'Email address',
    password: 'Password',
    rules: ['At least 8 characters', 'Upper and lower case', 'A number', 'A symbol'],
    terms: 'I accept the',
    termsLink: 'terms and conditions',
    privacy: 'and the',
    privacyLink: 'privacy policy',
    create: 'Create my account',
    haveAccount: 'Already have an account?',
    signIn: 'Sign in',
    codeTitle: 'Check your email',
    codeBody: 'We sent a code to',
    code: 'Confirmation code',
    confirm: 'Confirm',
    resend: 'Resend the code',
    resendIn: 'Resend in',
    resent: 'A new code has been sent.',
    wrongEmail: 'Wrong address?',
    restart: 'Start again',
    errors: {
      account_exists: 'An account already exists with this address. Sign in to continue.',
      weak_password: 'This password does not meet the rules below.',
      terms_required: 'Please accept the terms and conditions.',
      rate_limited: 'Too many attempts. Try again in an hour.',
      signup_unavailable: 'Sign-up is temporarily unavailable. Write to us at contact@cyrn.fr.',
      auth_unavailable: 'The account service is not responding. Try again in a moment.',
      invalid_code: 'Incorrect code.',
      too_many_attempts: 'Too many incorrect codes. Ask for a new code.',
      too_many_codes: 'Too many codes sent. Try again later.',
      resend_too_soon: 'Wait a little before asking for another code.',
      signup_expired: 'This sign-up has expired. Start again.',
      generic: 'Something went wrong.',
    },
  },
} as const;

type ErrorKey = keyof (typeof T)['fr']['errors'];

function errorKey(err: unknown): ErrorKey {
  if (err instanceof ApiError) {
    const code = (err.data as { detail?: { code?: string } } | null)?.detail?.code;
    if (code && code in T.fr.errors) return code as ErrorKey;
  }
  return 'generic';
}

const passwordChecks = (pw: string) => [
  pw.length >= 8,
  /[a-z]/.test(pw) && /[A-Z]/.test(pw),
  /[0-9]/.test(pw),
  /[^A-Za-z0-9]/.test(pw),
];

export function SignupPage() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const pick = (params.get('lang') || navigator.language || 'fr').slice(0, 2).toLowerCase();
  const lang: Lang = pick === 'en' ? 'en' : 'fr';
  const t = T[lang];
  const returnTo = params.get('return_to') || '/';
  // A suggested address (e.g. from an emailed report link), carried in the
  // checkout URL. Only a default: the customer can change it, and it is
  // confirmed by code like any other.
  const suggestedEmail = useMemo(() => {
    const own = params.get('email');
    if (own) return own;
    try {
      return new URL(returnTo, window.location.origin).searchParams.get('email') || '';
    } catch {
      return '';
    }
  }, [params, returnTo]);

  const [step, setStep] = useState<'loading' | 'form' | 'code'>('loading');
  const [form, setForm] = useState({
    given_name: '',
    family_name: '',
    email: suggestedEmail.slice(0, 254),
    password: '',
  });
  const [terms, setTerms] = useState(false);
  const [pending, setPending] = useState<SignupView | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<ErrorKey | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());

  // Resume a sign-up interrupted by a reload or a trip to the mailbox.
  useEffect(() => {
    api.signup
      .get()
      .then((v) => {
        setPending(v);
        setStep('code');
      })
      .catch(() => setStep('form'));
  }, []);

  useEffect(() => {
    if (step !== 'code') return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [step]);

  const checks = passwordChecks(form.password);
  const canSubmit =
    form.given_name.trim() && form.family_name.trim() && form.email.includes('@') && checks.every(Boolean) && terms;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || busy) return;
    setBusy(true);
    setError(null);
    try {
      const v = await api.signup.start({ ...form, locale: lang, return_to: returnTo, accept_terms: terms });
      setPending(v);
      setStep('code');
    } catch (err) {
      setError(errorKey(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim() || busy) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await api.signup.verify(code.trim());
      window.location.href = res.login_url;
    } catch (err) {
      const key = errorKey(err);
      if (key === 'signup_expired') setStep('form');
      setError(key);
      setBusy(false);
    }
  };

  const resend = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      setPending(await api.signup.resend());
      setNotice(t.resent);
      setCode('');
    } catch (err) {
      setError(errorKey(err));
    } finally {
      setBusy(false);
    }
  };

  const signInUrl = api.auth.loginReturnUrl(returnTo, undefined, form.email.includes('@') ? form.email : undefined);
  const waitSeconds = pending?.can_resend_at
    ? Math.max(0, Math.ceil((new Date(pending.can_resend_at).getTime() - now) / 1000))
    : 0;

  const link = (href: string, label: string) => (
    <a href={href} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>
      {label}
    </a>
  );

  const errorBox = error && (
    <Alert tone="danger" icon={<AlertCircle size={18} />}>
      {t.errors[error]}
      {error === 'account_exists' && (
        <>
          {' '}
          <a href={signInUrl} style={{ color: 'inherit', fontWeight: 600 }}>{t.signIn}</a>
        </>
      )}
    </Alert>
  );

  let body: React.ReactNode = null;
  if (step === 'form') {
    body = (
      <Card padding="var(--space-6)" glow>
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <div>
            <h1 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--fw-semibold)' }}>{t.title}</h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', marginTop: 4 }}>{t.subtitle}</p>
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
            <Input label={t.givenName} autoComplete="given-name" required value={form.given_name}
              onChange={(e) => setForm({ ...form, given_name: e.target.value })} />
            <Input label={t.familyName} autoComplete="family-name" required value={form.family_name}
              onChange={(e) => setForm({ ...form, family_name: e.target.value })} />
          </div>
          <Input label={t.email} type="email" autoComplete="email" required value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Input label={t.password} type="password" autoComplete="new-password" required value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })} />
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
              {t.rules.map((rule, i) => (
                <li key={rule} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '12px',
                  color: checks[i] ? 'var(--success)' : 'var(--text-tertiary)' }}>
                  {checks[i] ? <Check size={13} /> : <X size={13} />} {rule}
                </li>
              ))}
            </ul>
          </div>
          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
            <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} style={{ marginTop: 3 }} />
            <span>
              {t.terms} {link('https://cyrn.fr/conditions-generales.html', t.termsLink)} {t.privacy}{' '}
              {link('https://cyrn.fr/confidentialite.html', t.privacyLink)}.
            </span>
          </label>
          {errorBox}
          <Button type="submit" size="lg" icon={<ArrowRight size={18} />} disabled={!canSubmit || busy} style={{ width: '100%' }}>
            {t.create}
          </Button>
          <p style={{ textAlign: 'center', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
            {t.haveAccount} <a href={signInUrl} style={{ color: 'var(--accent)' }}>{t.signIn}</a>
          </p>
        </form>
      </Card>
    );
  } else if (step === 'code' && pending) {
    body = (
      <Card padding="var(--space-6)" glow>
        <form onSubmit={verify} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <MailCheck size={28} color="var(--accent)" />
          <div>
            <h1 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--fw-semibold)' }}>{t.codeTitle}</h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', marginTop: 4 }}>
              {t.codeBody} <strong>{pending.email}</strong>
            </p>
          </div>
          <Input label={t.code} mono autoFocus autoComplete="one-time-code" value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())} />
          {errorBox}
          {notice && <Alert tone="success">{notice}</Alert>}
          <Button type="submit" size="lg" icon={<ArrowRight size={18} />} disabled={!code.trim() || busy} style={{ width: '100%' }}>
            {t.confirm}
          </Button>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--text-sm)' }}>
            <Button type="button" variant="ghost" size="sm" disabled={busy || waitSeconds > 0} onClick={resend}>
              {waitSeconds > 0 ? `${t.resendIn} ${waitSeconds} s` : t.resend}
            </Button>
            <Button type="button" variant="ghost" size="sm" disabled={busy}
              onClick={() => { setStep('form'); setError(null); setCode(''); }}>
              {t.wrongEmail} {t.restart}
            </Button>
          </div>
        </form>
      </Card>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: 'var(--space-6)', background: 'radial-gradient(ellipse at top, #111927 0%, #070b11 100%)' }}>
      <div style={{ width: '100%', maxWidth: 480, display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Logo variant="stacked" size={44} light="beams" />
        </div>
        {body}
      </div>
    </div>
  );
}
